import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axios } from '../hooks/useAuth';
import { useAuth } from '../hooks/useAuth';
import { useAudioSync, useRecorder, useMixer } from '../hooks/useAudioSync';
import { useLyrics } from '../hooks/useLyrics';
import { useSocket } from '../hooks/useSocket';
import LyricDisplay from '../components/LyricDisplay';
import AudioPlayer from '../components/AudioPlayer';
import RecordingControls from '../components/RecordingControls';
import MixerControls from '../components/MixerControls';
import '../styles/singroom.css';

// Stages: 'idle' | 'recording' | 'review' | 'mixing' | 'saved'
const STAGE = { IDLE: 'idle', RECORDING: 'recording', REVIEW: 'review', MIXING: 'mixing', SAVED: 'saved' };

export default function SingRoomPage() {
  const { songId } = useParams();
  const { user } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();

  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roomId] = useState(`room-${songId}-${Date.now()}`);
  const [stage, setStage] = useState(STAGE.IDLE);
  const [showLyrics, setShowLyrics] = useState(true);
  const [recordedUrl, setRecordedUrl] = useState(null);

  // Mixer state
  const [backingVolume, setBackingVolume] = useState(0.8);
  const [vocalVolume, setVocalVolume] = useState(1.2);
  const [syncOffset, setSyncOffset] = useState(0);

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load song
  useEffect(() => {
    axios.get(`/api/songs/${songId}`)
      .then(res => {
        setSong(res.data.song);
        axios.post(`/api/songs/${songId}/view`).catch(() => {});
      })
      .catch(() => setError('Song not found'))
      .finally(() => setLoading(false));
  }, [songId]);

  // Socket room
  useEffect(() => {
    if (!socket || !user) return;
    socket.emit('room:join', {
      roomId,
      userId: user.id,
      displayName: user.displayName || user.username,
      emoji: user.emoji,
    });
    return () => socket.emit('room:leave', { roomId });
  }, [socket, roomId, user]);

  const apiBase = process.env.REACT_APP_API_URL || '';
  const audioUrl = song ? `${apiBase}${song.audioUrl}` : null;

  const { isPlaying, currentTime, duration, isLoading: audioLoading, togglePlay, seek, stop,
    volume, setVolume } = useAudioSync({
    songUrl: audioUrl,
    onTimeUpdate: (time, playing) => {
      if (socket && roomId) {
        socket.emit('audio:timeupdate', { roomId, currentTime: time, isPlaying: playing });
      }
    },
  });

  const { lyrics, currentLyricIndex, currentLyric, nextLyric } = useLyrics({
    songId, roomId, currentTime, isPlaying,
  });

  const {
    isRecording, recordedBlob, recordingTime, micPermission,
    error: recError, requestPermission, startRecording, stopRecording, discardRecording,
  } = useRecorder();

  const { mix, isMixing, mixProgress, mixError } = useMixer();

  // Build object URL from recorded blob
  useEffect(() => {
    if (recordedBlob) {
      const url = URL.createObjectURL(recordedBlob);
      setRecordedUrl(url);
      setStage(STAGE.REVIEW);
      return () => URL.revokeObjectURL(url);
    }
  }, [recordedBlob]);

  // Start recording
  const handleStartRecording = async () => {
    if (micPermission !== 'granted') {
      const ok = await requestPermission();
      if (!ok) return;
    }
    seek(0);
    setTimeout(() => {
      startRecording();
      if (!isPlaying) togglePlay();
      setStage(STAGE.RECORDING);
    }, 100);
  };

  // Stop recording
  const handleStopRecording = () => {
    stopRecording();
    stop();
    // stage transitions to REVIEW via useEffect above
  };

  // Discard and re-record
  const handleDiscard = () => {
    discardRecording();
    setRecordedUrl(null);
    setSyncOffset(0);
    setBackingVolume(0.8);
    setVocalVolume(1.2);
    setStage(STAGE.IDLE);
  };

  // Mix and save
  const handleSave = async () => {
    if (!recordedBlob || !song) return;
    setSaving(true);
    setStage(STAGE.MIXING);
    setError('');

    try {
      // Mix in the browser
      const mixedBlob = await mix({
        backingTrackUrl: audioUrl,
        vocalBlob: recordedBlob,
        backingVolume,
        vocalVolume,
        syncOffset,
      });

      if (!mixedBlob) throw new Error('Mixing failed');

      // Create compilation
      const compRes = await axios.post('/api/compilations', {
        songId: song.id,
        title: `${user.displayName || user.username}'s ${song.title} Cover`,
      });
      const comp = compRes.data.compilation;

      // Upload the mixed WAV as the track
      const formData = new FormData();
      formData.append('audio', mixedBlob, 'mixed.wav');
      formData.append('syncOffset', 0); // Already baked into the mix
      formData.append('label', `${user.displayName || user.username}'s Mix`);
      formData.append('volume', 1.0);

      await axios.post(`/api/compilations/${comp.id}/tracks`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setStage(STAGE.SAVED);
      setSaveSuccess(true);
      setTimeout(() => navigate(`/compilation/${comp.id}`), 2000);

    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save recording');
      setStage(STAGE.REVIEW);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="sing-room sing-loading">
        <div className="loading-mic animate-float">🎤</div>
        <p>Loading song...</p>
      </div>
    );
  }

  if (error && !song) {
    return (
      <div className="sing-room sing-error">
        <div style={{ fontSize: 48 }}>😢</div>
        <h2>{error}</h2>
        <button className="btn btn-primary" onClick={() => navigate('/songs')}>Browse Songs</button>
      </div>
    );
  }

  return (
    <div className="sing-room page-enter">
      <div className="sing-glow" />

      {/* Header */}
      <div className="sing-header">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/songs')}>← Back</button>
        <div className="sing-song-info">
          <h1 className="sing-title">{song.title}</h1>
          <p className="sing-artist">by {song.artist}</p>
        </div>
        <button
          className={`btn btn-sm ${showLyrics ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setShowLyrics(!showLyrics)}
        >
          📝 Lyrics
        </button>
      </div>

      {/* Stage */}
      <div className="sing-stage">
        {showLyrics && (
          <div className="sing-lyrics-panel">
            <LyricDisplay
              lyrics={lyrics}
              currentLyricIndex={currentLyricIndex}
              currentLyric={currentLyric}
              nextLyric={nextLyric}
              isPlaying={isPlaying}
            />
          </div>
        )}

        <div className="sing-controls-panel">

          {/* Audio Player — hide during mixing/saved */}
          {stage !== STAGE.MIXING && stage !== STAGE.SAVED && (
            <div className="card sing-player-card">
              <AudioPlayer
                isPlaying={isPlaying}
                currentTime={currentTime}
                duration={duration}
                isLoading={audioLoading}
                onTogglePlay={togglePlay}
                onSeek={seek}
                volume={volume}
                onVolumeChange={setVolume}
              />
            </div>
          )}

          {/* IDLE / RECORDING state */}
          {(stage === STAGE.IDLE || stage === STAGE.RECORDING) && (
            <div className="card sing-record-card">
              <div className="record-card-header">
                <h3>🎙️ Your Recording</h3>
                {isRecording && (
                  <div className="recording-indicator">
                    <span className="rec-dot" />
                    REC {String(Math.floor(recordingTime / 60)).padStart(2, '0')}:
                    {String(recordingTime % 60).padStart(2, '0')}
                  </div>
                )}
              </div>
              {recError && <div className="alert alert-error">{recError}</div>}
              <RecordingControls
                isRecording={isRecording}
                recordedBlob={null}
                recordedUrl={null}
                isPlaying={isPlaying}
                syncOffset={syncOffset}
                onSyncOffsetChange={setSyncOffset}
                onStartRecording={handleStartRecording}
                onStopRecording={handleStopRecording}
                onDiscard={handleDiscard}
                onSave={() => {}}
              />
            </div>
          )}

          {/* REVIEW state — playback + mixer */}
          {stage === STAGE.REVIEW && recordedBlob && (
            <div className="card sing-record-card">
              <div className="record-card-header">
                <h3>✅ Review Your Recording</h3>
              </div>

              {error && <div className="alert alert-error">{error}</div>}

              {/* Raw vocal playback */}
              <div style={{ marginBottom: 16 }}>
                <p className="text-sm text-secondary" style={{ marginBottom: 6 }}>
                  Your vocal track (raw):
                </p>
                <audio controls src={recordedUrl} style={{ width: '100%' }} />
              </div>

              {/* Mixer */}
              <MixerControls
                backingTrackUrl={audioUrl}
                vocalBlob={recordedBlob}
                backingVolume={backingVolume}
                vocalVolume={vocalVolume}
                syncOffset={syncOffset}
                onBackingVolumeChange={setBackingVolume}
                onVocalVolumeChange={setVocalVolume}
                onSyncOffsetChange={setSyncOffset}
                isMixing={false}
                mixProgress={0}
              />

              {/* Action buttons */}
              <div className="playback-controls" style={{ marginTop: 16 }}>
                <button className="btn btn-danger flex-1" onClick={handleDiscard}>
                  🗑️ Re-record
                </button>
                <button className="btn btn-primary flex-1" onClick={handleSave} disabled={saving}>
                  💾 Mix & Save
                </button>
              </div>
            </div>
          )}

          {/* MIXING state */}
          {stage === STAGE.MIXING && (
            <div className="card sing-record-card" style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🎛️</div>
              <h3>Mixing your track...</h3>
              <p className="text-secondary text-sm" style={{ marginBottom: 20 }}>
                Blending backing track + vocals in your browser
              </p>
              <div className="mix-progress">
                <div className="mix-progress-bar" style={{ width: `${mixProgress}%` }} />
              </div>
              <p className="text-muted text-sm" style={{ marginTop: 8 }}>{mixProgress}%</p>
            </div>
          )}

          {/* SAVED state */}
          {stage === STAGE.SAVED && (
            <div className="card sing-record-card" style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
              <h3>Mix saved!</h3>
              <p className="text-secondary text-sm">Redirecting to your compilation...</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
